import { useState } from "react";
import { Mic2, ExternalLink } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { HOST, CAST, CAST_UNCONFIRMED } from "../data/content";

function CastCard({ member, index }) {
  const [flipped, setFlipped] = useState(false);
  const id = `cast-card-${member.name.toLowerCase().replace(/[\s.]+/g, "-")}`;
  return (
    <Reveal delay={(index % 8) * 0.05}>
      <div
        className={`flip-card aspect-[3/4] ${flipped ? "flipped" : ""}`}
        data-testid={id}
        tabIndex={0}
        role="button"
        aria-pressed={flipped}
        aria-label={`${member.name} — press to see credits`}
        onClick={() => setFlipped(!flipped)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setFlipped(!flipped); } }}
      >
        <div className="flip-inner">
          <div className="flip-face border border-white/10 bg-[#181824]">
            {member.img ? (
              <img src={member.img} alt={`${member.name} headshot`} loading="lazy" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-3 bg-gradient-to-b from-[#181824] to-[#0A0A0E]">
                <Mic2 size={40} className="text-[#E11D48]" />
                <span className="text-xs uppercase tracking-widest text-gray-500">Photo coming soon</span>
              </div>
            )}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-4 pt-12">
              <p className="font-semibold text-white">{member.name}</p>
              <p className="text-xs text-[#F59E0B]">{member.role}</p>
            </div>
          </div>
          <div className="flip-face flip-back border border-[#F59E0B]/40 bg-[#181824] p-5 flex flex-col justify-center gap-3">
            <p className="font-display text-2xl text-white">{member.name}</p>
            <p className="text-xs font-semibold uppercase tracking-widest text-[#F59E0B]">{member.role}</p>
            {member.note && <p className="text-xs text-gray-400">{member.note}</p>}
            {member.imdb ? (
              <a href={member.imdb} target="_blank" rel="noopener noreferrer" data-testid={`${id}-imdb`}
                className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-[#F59E0B] hover:text-white"
                onClick={(e) => e.stopPropagation()}>
                IMDb profile <ExternalLink size={14} />
              </a>
            ) : (
              <p className="mt-2 text-xs text-gray-500">Profile to confirm</p>
            )}
          </div>
        </div>
      </div>
    </Reveal>
  );
}

export default function Cast() {
  return (
    <main className="pt-16" data-testid="cast-page">
      <Seo title="Cast" description="Meet the cast of Laugh at L.A. — hosted by Todd Bridges with Leslie Jones, Kel Mitchell, Debra Wilson and L.A.'s stand-up and sketch talent." />
      <section className="spotlight-bg border-b border-white/5">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-20 sm:py-24">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#F59E0B]">Cast</p>
            <h1 className="mt-3 font-display text-5xl sm:text-7xl text-white neon-red">The names on the marquee.</h1>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-16">
        <Reveal>
          <div className="grid lg:grid-cols-[420px_1fr] gap-10 items-center rounded-2xl border border-[#F59E0B]/30 bg-gradient-to-br from-[#181824] to-[#0A0A0E] p-8 sm:p-12" data-testid="host-feature-card">
            <div className="relative">
              <div className="absolute -inset-3 rounded-2xl bg-[#E11D48]/20 blur-2xl" aria-hidden="true" />
              <img src={HOST.img} alt={`${HOST.name}, host`} className="relative rounded-xl border border-white/10 w-full object-cover aspect-[3/4]" loading="lazy" />
            </div>
            <div>
              <p className="inline-block rounded-full bg-gradient-to-r from-amber-500 to-yellow-600 px-4 py-1 text-xs font-bold uppercase tracking-wider text-black">Host</p>
              <h2 className="mt-4 font-display text-5xl sm:text-6xl text-white">{HOST.name}</h2>
              <p className="mt-2 text-[#F59E0B] text-sm font-semibold uppercase tracking-widest">{HOST.credit}</p>
              <p className="mt-5 text-gray-300 leading-relaxed max-w-lg">
                The original host of Laugh at L.A., Todd Bridges introduces the comics, sets up the sketches, and keeps the room rolling all night.
              </p>
              <a href={HOST.imdb} target="_blank" rel="noopener noreferrer" data-testid="host-card-imdb"
                className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#F59E0B]/60 px-6 py-2.5 text-sm font-semibold text-[#F59E0B] hover:bg-[#F59E0B]/10 transition-colors">
                IMDb profile <ExternalLink size={14} />
              </a>
            </div>
          </div>
        </Reveal>

        <Reveal className="mt-16">
          <h2 className="font-display text-3xl sm:text-4xl text-white">Featured Talent & Sketch Cast</h2>
          <p className="mt-2 text-sm text-gray-400">Tap or press a card to see credits. IMDb links appear only where the match is confirmed.</p>
        </Reveal>
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
          {CAST.map((m, i) => <CastCard key={m.name} member={m} index={i} />)}
        </div>

        <Reveal className="mt-16">
          <h2 className="font-display text-3xl sm:text-4xl text-white">Profiles to Confirm</h2>
          <p className="mt-2 text-sm text-gray-400">Identified in production materials and episode credits. IMDb links go live once each profile is confirmed.</p>
        </Reveal>
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
          {CAST_UNCONFIRMED.map((m, i) => <CastCard key={m.name} member={m} index={i} />)}
        </div>
      </section>
    </main>
  );
}
